import { PlusOutlined } from '@ant-design/icons';
import { Form, Image, Typography, Upload, message } from 'antd';
import type { FormRule, UploadFile } from 'antd';
import { useEffect, useState } from 'react';
import tw from 'twin.macro';

import { useUploadImage } from '@/hooks/mutations/admin';
import { MAX_UPLOAD_IMAGE_SIZE } from '@/utils/uploader';

type Props = {
  name?: string;
  rules?: FormRule[];
} & (
  | {
      multiple: true;
      initialValue?: string[];
      maxCount: number;
      onChange?: (newValue: string[]) => void;
    }
  | {
      multiple: false;
      initialValue?: string;
      onChange?: (newValue: string) => void;
    }
);

const ImageUpload = ({
  name,
  rules,
  multiple,
  initialValue,
  onChange,
  ...rest
}: Props) => {
  const formInstance = Form.useFormInstance();
  // 이 컴포넌트가 antd Form 컴포넌트 내에 위치하면서 name을 할당하는 경우 antd useForm을 통해 관리되도록
  const isAntdForm = name && !!formInstance;
  const imageUpload = useUploadImage();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const isMultipleMode = multiple && 'maxCount' in rest;

  useEffect(() => {
    if (initialValue) {
      if (multiple) {
        setFileList(
          initialValue.map((value, index) => ({
            uid: value,
            name: `${index}`,
            status: 'done',
            url: value,
          }))
        );
      } else {
        setFileList([
          {
            uid: '-1',
            name: '1',
            status: 'done',
            url: initialValue,
          },
        ]);
      }
    }
  }, []);

  useEffect(() => {
    if (isAntdForm) {
      formInstance.setFieldValue(
        name,
        multiple
          ? fileList.map((item) => item?.response?.url ?? item?.url)
          : (fileList?.[0]?.response?.url ?? fileList?.[0]?.url ?? '')
      );
    }
  }, [fileList]);

  const uploader = (
    <>
      <Upload
        accept="image/jpg, image/png, image/jpeg"
        listType="picture-card"
        fileList={fileList}
        multiple={multiple}
        maxCount={isMultipleMode ? rest.maxCount : 1}
        beforeUpload={(_, addList) => {
          if (
            isMultipleMode &&
            fileList.length + addList.length > rest.maxCount
          ) {
            message.error(`최대 ${rest.maxCount}장 업로드 가능해요`);
            return false;
          }

          if (addList.some((file) => file.size > MAX_UPLOAD_IMAGE_SIZE)) {
            message.error('2MB 이하의 파일을 첨부해주세요.');
            return false;
          }

          return true;
        }}
        customRequest={async ({ file, onSuccess, onError, onProgress }) => {
          imageUpload.mutate({
            files: [file as File],
            onSuccess,
            onError,
            onProgress,
          });
        }}
        onChange={(e) => {
          const newFileList = e.fileList.filter(
            (file) =>
              (file.size ? file.size <= MAX_UPLOAD_IMAGE_SIZE : true) && // beforeUpload에서 false를 리턴하는 것은 api 요청만 안하는 것이지 onChange e.fileList에는 들어오기 때문에 걸러줘야 함
              !!file.status // beforeUpload에서 false를 반환한 경우 파일에 status가 할당되지 않기에 여기서 걸러낼 수 있음
          );

          setFileList(newFileList);
          if (multiple) {
            onChange?.(
              newFileList.map((item) => item?.response?.url ?? item?.url)
            );
          } else {
            onChange?.(
              newFileList?.[0]?.response?.url ?? newFileList?.[0]?.url
            );
          }
        }}
        onRemove={(targetFile) => {
          const newFileList = fileList.filter(
            (item) => item.uid !== targetFile.uid
          );
          setFileList(newFileList);

          if (multiple) {
            onChange?.(
              newFileList.map((item) => item?.response?.url ?? item?.url)
            );
          } else {
            onChange?.('');
          }
          return false;
        }}
        onPreview={(file) => {
          setPreviewUrl(file?.response?.url ?? file?.url ?? '');
        }}
      >
        <button style={{ border: 0, background: 'none' }} type="button">
          <PlusOutlined />
          <div>Upload</div>
        </button>
      </Upload>

      <Image
        wrapperStyle={{ display: 'none' }}
        preview={{
          visible: !!previewUrl,
          onVisibleChange: (visible) => !visible && setPreviewUrl(''),
        }}
        src={previewUrl}
      />
    </>
  );

  return (
    <div css={tw`flex flex-col gap-[10px]`}>
      {isAntdForm ? (
        <Form.Item name={name} rules={rules} valuePropName="_">
          {uploader}
        </Form.Item>
      ) : (
        uploader
      )}

      <Typography.Text type="secondary">
        이미지는 3:2 비율로 업로드해주세요 (2MB 이하, jpg)
      </Typography.Text>
    </div>
  );
};

export default ImageUpload;
